import { useCallback, useContext, useState, type FC } from 'react';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { TermsOfService } from './TermsOfService';
import { getStoredTosAccepted, setStoredTosAccepted } from '@/utils/storage';
import { TosLastUpdated } from '@/utils/tos';

// Try to get the user's last accepted date from localStorage.
// If we have a valid last accepted date and it's after than the last updated date,
// then we don't need to show the terms.
const hasUserAcceptedTerms = (userId: string) => {
  const lastAccepted = getStoredTosAccepted(userId);
  if (lastAccepted && !isNaN(Number(lastAccepted))) {
    const lastAcceptedDate = Number(lastAccepted);
    return lastAcceptedDate > TosLastUpdated;
  }
  return false;
};

/**
 * Checks if the current user has accepted the most recent terms of service.
 * If not, renders the TermsOfService; otherwise proceeds to render the Private content.
 */
export const TermsOfServiceChecker: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userInfo } = useContext(UserInfoContext);
  const userId = userInfo?.id || '';

  const [hasAccepted, setHasAccepted] = useState(() => hasUserAcceptedTerms(userId));

  // Handles the user accepting the terms by storing userId and current datetime (as epoch ms) in localStorage.
  const handleAccept = useCallback(() => {
    setStoredTosAccepted(userId, new Date().valueOf());
    setHasAccepted(true);
  }, [userId]);

  if (!userId) {
    return;
  }
  return hasAccepted ? children : <TermsOfService onAccept={handleAccept} />;
};
